import { connect } from 'react-redux';
import { push } from 'redux-first-history';
import parsePhoneNumber from 'libphonenumber-js';
import InfoSection from './component';
import {
  cancelRentAmountChange,
  getIreneDashboard,
  getRentalProtectionDisclaimers,
  requestPhoneVerificationCheck,
} from '@rental-manager/actions/requests';
import { cleanPhoneNumber } from '@common/utils/numbersUtils';
import { DEFAULT_ERROR, RENT_AMOUNT_UPDATE_CANCEL_SUCCESS } from '@rental-manager/constants/NotificationConstants';
import { saveMoveInCostsSettings, startNewTerm } from '@rental-manager/actions/OnboardingActions';
import { closeNotification, showModalNotification, showPageNotification } from '@common/actions/NotificationActions';
import { checkRequestActionSuccess } from '@common/paymentsUtils/api';
import paths from '@rental-manager/pathsManifest';
import FeaturePermissionTypes from '@rental-manager/constants/FeaturePermissionTypes';
import type { EditMoveInCostsInterface } from '@rental-manager/pages/Dashboard/types';
import { logError } from '@zg-rentals/log-error';

const mapStateToProps = (state) => {
  const {
    accountInfo,
    currentTerm,
    nextTerm,
    userEvent,
    allTransactions,
    leasesForProperty,
    featurePermissions,
    userConfig,
    listingDetails,
    isLatestRentPaymentFailed,
    shouldDisplayRentalProtectionFirstRentFailedBanner,
    listingAlias,
    phoneNumber,
  } = state.payments.landlord.property;

  const { rentTermsKey } = currentTerm;
  const rentTermsExtendPermissionCurrentTerm =
    featurePermissions?.[rentTermsKey]?.[FeaturePermissionTypes.RENT_TERMS_EXTEND] || {};
  const nextTermRentTermsKey = nextTerm?.rentTermsKey || '';
  const rentTermsExtendPermissionNextTerm =
    featurePermissions?.[nextTermRentTermsKey]?.[FeaturePermissionTypes.RENT_TERMS_EXTEND] || {};

  const { isRentGuaranteeEnabled } = userConfig;
  const rentGuarantee = featurePermissions?.[rentTermsKey]?.[FeaturePermissionTypes.RENT_GUARANTEE].exhausted;

  return {
    accountInfo,
    userEvent,
    currentTerm,
    nextTerm,
    allTransactions,
    featurePermissions,
    userConfig,
    today: new Date(),
    isLatestRentPaymentFailed,
    shouldDisplayRentalProtectionFirstRentFailedBanner,
    isRentGuaranteeEnabled,
    isRentalProtectionVariation: isRentGuaranteeEnabled && rentGuarantee,
    leasesForProperty,
    rentTermsExtendPermissionCurrentTerm,
    rentTermsExtendPermissionNextTerm,
    listingAlias,
    listingDetailsContactPhone: listingDetails.contactPhone,
    stripePhoneNumber: phoneNumber ? parsePhoneNumber(phoneNumber)?.nationalNumber : '',
  };
};

const mapDispatchToProps = (dispatch, { baseUrl, propertyId }) => ({
  goToNewTermPage: async (leasesForProperty): Promise<void> => {
    await dispatch(closeNotification());
    await dispatch(startNewTerm({ propertyId }));
    await dispatch(
      push({
        pathname:
          baseUrl +
          (leasesForProperty.length > 0 ? paths.onboarding.leaseSelectorPage : paths.onboarding.rentAmountPage),
        search: 'acceptTerms=true',
      }),
    );
  },
  gotToPhoneVerificationPage: async (): Promise<void> => {
    await dispatch(closeNotification());
    await dispatch(push(baseUrl + paths.dashboard.phoneVerificationPage));
  },
  goToOneTimePaymentPage: async (): Promise<void> => {
    await dispatch(closeNotification());
    await dispatch(push(baseUrl + paths.dashboard.createOneTimePaymentRequestPage));
  },
  goToStartNewTermPage: async (): Promise<void> => {
    await dispatch(closeNotification());
    await dispatch(push(baseUrl + paths.dashboard.setupNewTermPage));
  },
  goToEditPaymentLengthPage: async (rentTerms: any): Promise<void> => {
    await dispatch(closeNotification());
    await dispatch(
      push({
        pathname: baseUrl + paths.dashboard.editPaymentLengthPage,
        search: `rentTermsKey=${rentTerms.rentTermsKey}`,
      }),
    );
  },
  goToEditTermLengthPage: async (rentTerms: any): Promise<void> => {
    await dispatch(closeNotification());
    await dispatch(
      push({
        pathname: baseUrl + paths.dashboard.editTermLengthPage,
        search: `rentTermsKey=${rentTerms.rentTermsKey}`,
      }),
    );
  },
  goToEditRentAmountPage: async (
    rentTerms: any,
    isRentalProtectionVariation: boolean,
    listingAlias: string,
  ): Promise<void> => {
    await dispatch(closeNotification());
    if (isRentalProtectionVariation) {
      await dispatch(getRentalProtectionDisclaimers({ propertyId, listingAlias }));
    }
    await dispatch(
      push({
        pathname: baseUrl + paths.dashboard.editRentAmountPage,
        search: `rentTermsKey=${rentTerms.rentTermsKey}`,
      }),
    );
  },
  goToEditDueDatePage: async (rentTerms: any): Promise<void> => {
    await dispatch(closeNotification());
    await dispatch(
      push({
        pathname: baseUrl + paths.dashboard.editDueDatePage,
        search: `rentTermsKey=${rentTerms.rentTermsKey}`,
      }),
    );
  },
  goToEditTenantInfoPage: async (rentTerms: any): Promise<void> => {
    await dispatch(closeNotification());
    await dispatch(
      push({
        pathname: baseUrl + paths.dashboard.editTenantInfoPage,
        search: `rentTermsKey=${rentTerms.rentTermsKey}`,
      }),
    );
  },
  goToEditAccountInfoPage: async (): Promise<void> => {
    await dispatch(closeNotification());
    await dispatch(push(baseUrl + paths.dashboard.editAccountInfoPage));
  },
  cancelRentAmountChange: async ({ tenantFirstName, rentTermsKey }: any): Promise<any> => {
    try {
      await dispatch(closeNotification());
      const action = await dispatch(cancelRentAmountChange({ propertyId, rentTermsKey }));
      await checkRequestActionSuccess(action);
      await dispatch(push(baseUrl));
      await dispatch(getIreneDashboard({ propertyId }));
      return dispatch(showPageNotification(RENT_AMOUNT_UPDATE_CANCEL_SUCCESS, { tenantFirstName }));
    } catch (err) {
      logError({ error: err, context: 'cancelRentAmountChange' });
      await dispatch(showModalNotification(DEFAULT_ERROR));
      return { error: true };
    }
  },
  checkIsPhoneVerificationNeeded: async ({ listingDetailsContactPhone, stripePhoneNumber }): Promise<boolean> => {
    const action = await dispatch(
      requestPhoneVerificationCheck({
        propertyId,
        phoneNumber: cleanPhoneNumber(listingDetailsContactPhone || stripePhoneNumber),
      }),
    );
    await checkRequestActionSuccess(action);
    if (
      !action.payload ||
      !action.payload.response ||
      !action.payload.response.isSessionValid ||
      !action.payload.response.isVerified
    ) {
      return true;
    }
    return false;
  },

  addMoveInCosts: async (term: any): Promise<void> => {
    const payload = {
      term,
    };
    await dispatch(closeNotification());
    await dispatch(saveMoveInCostsSettings({ propertyId, ...payload }));
    await dispatch(push(baseUrl + paths.dashboard.createMoveInCostsPage));
  },
  cancelMoveInCostsModal: async ({ term, rentTermsKey }: EditMoveInCostsInterface): Promise<void> => {
    const payload = {
      term,
    };
    await dispatch(closeNotification());
    await dispatch(saveMoveInCostsSettings({ propertyId, ...payload }));
    await dispatch(
      push({
        pathname: baseUrl + paths.dashboard.cancelMoveInCostsRequest,
        search: `rentTermsKey=${rentTermsKey}`,
      }),
    );
  },
});

const mergeProps = (stateProps, dispatchProps) => ({
  ...stateProps,
  ...dispatchProps,
  goToEditRentAmountPage: (...args) =>
    dispatchProps.goToEditRentAmountPage(...args, stateProps.isRentalProtectionVariation, stateProps.listingAlias),
  goToNewTermPage: () =>
    dispatchProps.goToNewTermPage(
      stateProps.leasesForProperty,
      stateProps.isRentGuaranteeEnabled,
      stateProps.listingAlias,
    ),
  checkIsPhoneVerificationNeeded: () =>
    dispatchProps.checkIsPhoneVerificationNeeded({
      listingDetailsContactPhone: stateProps.listingDetailsContactPhone,
      stripePhoneNumber: stateProps.stripePhoneNumber,
    }),
});

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(InfoSection);
